<template>
  <div class="booking-management">
    <!-- 查询条件区域 -->
    <el-card class="search-card" shadow="never">
      <el-form :model="searchForm" label-width="100px" class="search-form">
        <el-row :gutter="20">
          <el-col :xs="24" :sm="12" :md="8" :lg="6">
            <el-form-item label="预约单号">
              <el-input
                v-model="searchForm.reservationNo"
                placeholder="请输入预约单号"
                clearable
              />
            </el-form-item>
          </el-col>

          <el-col :xs="24" :sm="12" :md="8" :lg="6">
            <el-form-item label="意向车型">
              <el-select
                v-model="searchForm.carModel"
                placeholder="请选择意向车型"
                clearable
                filterable
                style="width: 100%"
              >
                <el-option
                  v-for="item in carModelOptions"
                  :key="item.value"
                  :label="item.label"
                  :value="item.value"
                />
              </el-select>
            </el-form-item>
          </el-col>
          
          <el-col :xs="24" :sm="12" :md="8" :lg="6">
            <el-form-item label="城市">
              <el-select
                v-model="searchForm.city"
                placeholder="请选择城市"
                clearable
                filterable
                style="width: 100%"
              >
                <el-option
                  v-for="item in cityList"
                  :key="item.code"
                  :label="item.name"
                  :value="item.code"
                />
              </el-select>
            </el-form-item>
          </el-col>
          
          <el-col :xs="24" :sm="12" :md="8" :lg="6">
            <el-form-item label="手机号">
              <el-input
                v-model="searchForm.phone"
                placeholder="请输入手机号"
                clearable
              />
            </el-form-item>
          </el-col>
          
          <el-col :xs="24" :sm="12" :md="12" :lg="8">
            <el-form-item label="试驾时间">
              <el-date-picker
                v-model="searchForm.testDriveTimeRange"
                type="datetimerange"
                range-separator="至"
                start-placeholder="开始时间"
                end-placeholder="结束时间"
                style="width: 100%"
                clearable
                :default-time="defaultTime"
              />
            </el-form-item>
          </el-col>
        </el-row>
        
        <el-row>
          <el-col :span="24" class="search-buttons">
            <el-button type="primary" :icon="Search" @click="handleSearch">查询</el-button>
            <el-button :icon="Refresh" @click="handleReset">清空</el-button>
          </el-col>
        </el-row>
      </el-form>
    </el-card>

    <!-- 数据列表区域 -->
    <el-card class="table-card" shadow="never">
      <el-table
        :data="tableData"
        style="width: 100%"
        v-loading="loading"
        stripe
      >
        <el-table-column prop="reservationNo" label="预约单号" min-width="200" show-overflow-tooltip />
        <el-table-column label="意向车型" min-width="130">
          <template #default="{ row }">
            {{ getCarModelLabel(row.carModel) }}
          </template>
        </el-table-column>
        <el-table-column label="城市" min-width="100">
          <template #default="{ row }">
            {{ getCityName(row.city) }}
          </template>
        </el-table-column>
        <el-table-column prop="phone" label="手机号" min-width="120" />
        <el-table-column prop="reservationTestDriveTime" label="试驾时间" min-width="160" />
        <el-table-column label="上门地址" min-width="150">
          <template #default="{ row }">
            <el-tooltip
              :content="row.address"
              placement="top"
              :disabled="!row.address"
              popper-class="address-tooltip"
            >
              <div class="ellipsis-cell">{{ row.address }}</div>
            </el-tooltip>
          </template>
        </el-table-column>
        <el-table-column label="备注" min-width="130">
          <template #default="{ row }">
            <el-tooltip
              :content="row.remark"
              placement="top"
              :disabled="!row.remark"
              popper-class="remark-tooltip"
            >
              <div class="ellipsis-cell">{{ row.remark }}</div>
            </el-tooltip>
          </template>
        </el-table-column>
        <el-table-column label="操作" width="180" fixed="right">
          <template #default="{ row }">
            <el-button type="primary" link :icon="View" @click="handleDetail(row)">详情</el-button>
            <el-button type="primary" link :icon="Edit" @click="handleEdit(row)">修改</el-button>
          </template>
        </el-table-column>
      </el-table>

      <!-- 分页 -->
      <div class="pagination">
        <el-pagination
          v-model:current-page="pagination.currentPage"
          v-model:page-size="pagination.pageSize"
          :page-sizes="[10, 20, 50, 100]"
          :total="pagination.total"
          layout="total, sizes, prev, pager, next, jumper"
          @size-change="handleSizeChange"
          @current-change="handleCurrentChange"
        />
      </div>
    </el-card>
  </div>
</template>

<script>
export default {
  name: 'BookingManagement'
}
</script>

<script setup>
import { ref, reactive, onMounted, onUnmounted } from 'vue'
import { useRouter } from 'vue-router'
import { Search, Refresh, View, Edit } from '@element-plus/icons-vue'
import { ElMessage } from 'element-plus'
import { getReservationPage } from '@/api/reservation'
import { getCityList, getBrandCarModeList } from '@/api/city'

const router = useRouter()

// 本地存储的key
const STORAGE_KEY = 'booking_search_history'

// 默认时间：开始时间 00:00:00，结束时间 23:59:59
const defaultTime = [
  new Date(2000, 1, 1, 0, 0, 0),
  new Date(2000, 1, 1, 23, 59, 59)
]

// 城市列表
const cityList = ref([])

// 品牌列表
const brandCarModeList = ref([])

// 车型选项列表（拼凑后的）
const carModelOptions = ref([])

// 查询表单
const searchForm = reactive({
  reservationNo: '',
  carModel: '',
  city: '',
  phone: '',
  testDriveTimeRange: ''
})

// 从本地存储加载历史查询条件
const loadSearchHistory = () => {
  try {
    const history = localStorage.getItem(STORAGE_KEY)
    if (history) {
      const data = JSON.parse(history)
      // 恢复查询条件
      Object.keys(data).forEach(key => {
        if (key in searchForm) {
          searchForm[key] = data[key]
        }
      })
      console.log('已加载历史查询条件：', data)
    }
  } catch (error) {
    console.error('加载历史查询条件失败：', error)
  }
}

// 保存查询条件到本地存储
const saveSearchHistory = () => {
  try {
    const data = {
      reservationNo: searchForm.reservationNo,
      carModel: searchForm.carModel,
      city: searchForm.city,
      phone: searchForm.phone,
      testDriveTimeRange: searchForm.testDriveTimeRange
    }
    localStorage.setItem(STORAGE_KEY, JSON.stringify(data))
    console.log('已保存查询条件：', data)
  } catch (error) {
    console.error('保存查询条件失败：', error)
  }
}

// 表格数据
const tableData = ref([])

// 分页
const pagination = reactive({
  currentPage: 1,
  pageSize: 10,
  total: 0
})

// 加载状态
const loading = ref(false)

// 查询
const handleSearch = async () => {
  loading.value = true

  // 组装查询参数
  const params = {
    pageNum: pagination.currentPage,
    pageSize: pagination.pageSize,
    reservationNo: searchForm.reservationNo || undefined,
    carModel: searchForm.carModel || undefined,
    city: searchForm.city || undefined,
    phone: searchForm.phone || undefined
  }

  // 如果选择了时间范围，提取开始和结束时间
  if (searchForm.testDriveTimeRange && searchForm.testDriveTimeRange.length === 2) {
    params.reservationTestDriveTimeStart = formatTime(searchForm.testDriveTimeRange[0])
    params.reservationTestDriveTimeEnd = formatTime(searchForm.testDriveTimeRange[1])
  }

  // 保存查询条件到本地存储
  saveSearchHistory()

  try {
    const res = await getReservationPage(params)
    const page = res.data || {}
    tableData.value = page.records || []
    pagination.total = page.total || 0
  } catch (error) {
    console.error('查询预约单失败：', error)
  } finally {
    loading.value = false
  }
}

// 将日期对象格式化为 yyyy-MM-dd HH:mm:ss
const formatTime = (date) => {
  if (!date) return undefined
  const d = new Date(date)
  const pad = (n) => String(n).padStart(2, '0')
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())} ${pad(d.getHours())}:${pad(d.getMinutes())}:${pad(d.getSeconds())}`
}

// 清空
const handleReset = () => {
  searchForm.reservationNo = ''
  searchForm.carModel = ''
  searchForm.city = ''
  searchForm.phone = ''
  searchForm.testDriveTimeRange = ''

  // 清空本地存储
  localStorage.removeItem(STORAGE_KEY)

  // 重置到第一页并重新查询
  pagination.currentPage = 1
  handleSearch()
  ElMessage.info('已清空查询条件')
}

// 详情
const handleDetail = (row) => {
  router.push({
    path: '/home/booking/detail',
    query: {
      id: row.id,
      mode: 'detail'
    }
  })
}

// 修改
const handleEdit = (row) => {
  router.push({
    path: '/home/booking/detail',
    query: {
      id: row.id,
      mode: 'edit'
    }
  })
}

// 分页大小改变
const handleSizeChange = (val) => {
  pagination.pageSize = val
  pagination.currentPage = 1
  handleSearch()
}

// 当前页改变
const handleCurrentChange = (val) => {
  pagination.currentPage = val
  handleSearch()
}

// 全局回车触发查询（无需聚焦输入框）
const handleEnterKey = (e) => {
  if (e.key === 'Enter') {
    handleSearch()
  }
}

// 加载城市列表
const loadCityList = async () => {
  try {
    const res = await getCityList()
    if (res.code === 200 && res.success) {
      cityList.value = res.data || []
      console.log('城市列表加载成功：', cityList.value)
    }
  } catch (error) {
    console.error('获取城市列表失败：', error)
  }
}

// 加载品牌列表
const loadBrandCarModeList = async () => {
  try {
    const res = await getBrandCarModeList()
    if (res.code === 200 && res.success) {
      brandCarModeList.value = res.data || []
      console.log('品牌列表加载成功：', brandCarModeList.value)
      
      // 处理车型选项：将品牌名称和车型名称拼凑
      carModelOptions.value = []
      brandCarModeList.value.forEach(brand => {
        if (brand.carModel && Array.isArray(brand.carModel)) {
          brand.carModel.forEach(model => {
            carModelOptions.value.push({
              label: `${brand.brandName} ${model.modelName}`,  // 显示：乐道 L60
              value: model.modelName,  // 查询值：L60
              brandName: brand.brandName,
              brandCode: brand.brandCode,
              modelImage: model.modelImage
            })
          })
        }
      })
      console.log('车型选项列表：', carModelOptions.value)
    }
  } catch (error) {
    console.error('获取品牌列表失败：', error)
  }
}

// 根据城市code获取城市名称
const getCityName = (cityCode) => {
  if (!cityCode) return ''
  const city = cityList.value.find(item => item.code === cityCode)
  return city ? city.name : cityCode
}

// 根据车型modelName获取完整显示名称（品牌+车型）
const getCarModelLabel = (modelName) => {
  if (!modelName) return ''
  const carModel = carModelOptions.value.find(item => item.value === modelName)
  return carModel ? carModel.label : modelName
}

// 组件挂载时加载历史查询条件并查询
onMounted(() => {
  loadCityList()
  loadBrandCarModeList()
  loadSearchHistory()
  handleSearch()
  window.addEventListener('keyup', handleEnterKey)
})

onUnmounted(() => {
  window.removeEventListener('keyup', handleEnterKey)
})
</script>

<style scoped>
.booking-management {
  padding: 20px;
}

.search-card {
  margin-bottom: 20px;
}

.search-form {
  padding: 10px 0;
}

.search-buttons {
  text-align: right;
  padding-right: 10px;
}

.table-card {
  margin-bottom: 20px;
}

.pagination {
  margin-top: 20px;
  display: flex;
  justify-content: flex-end;
}

:deep(.el-card__body) {
  padding: 20px;
}

:deep(.el-form-item) {
  margin-bottom: 18px;
}

:deep(.el-table) {
  font-size: 14px;
}

:deep(.el-table th) {
  background-color: #f5f7fa;
  color: #606266;
  font-weight: 600;
}

:deep(.el-button + .el-button) {
  margin-left: 8px;
}

/* 单元格省略号样式 - 统一处理 */
.ellipsis-cell {
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  width: 100%;
}

/* 上门地址单元格样式 */
.address-cell {
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}
</style>

<style>
/* 备注提示框样式 - 自动换行 */
.remark-tooltip {
  max-width: 300px !important;
  white-space: normal !important;
  word-wrap: break-word !important;
  word-break: break-all !important;
  line-height: 1.6 !important;
}

/* 上门地址提示框样式 - 自动换行 */
.address-tooltip {
  max-width: 300px !important;
  white-space: normal !important;
  word-wrap: break-word !important;
  word-break: break-all !important;
  line-height: 1.6 !important;
}
</style>
